/**
 * Manages device registrations for Web Push.
 */
import axios from 'axios';
import remoteLoggerApi from '@/api/remoteLoggerApi';
import { LOCAL_STORAGE_DEVICE_UUID, LOCAL_STORAGE_WEBPUSH_SUBSCRIPTION } from '@/common/constants';
import config from '@/common/config';

export default {
  /**
   * Send the service-worker webpush subscription endpoint to the backend to store it into the database.
   * The deviceController, resp. the deviceService of the api stores the subscription to the device_status table
   * This is going to work only when running as PWA.
   * @param { ServiceWorkerRegistration } swRegistration
   * @param publicVapidKey
   * @param resubscribeOnFailure
   */
  async registerDeviceForWebPush(swRegistration, publicVapidKey, resubscribeOnFailure = true) {
    if (!publicVapidKey) {
      throw Error('registerDeviceForWebPush failed since no publicVapidKey defined');
    }

    if (!swRegistration) {
      throw Error('webpush subscription not performed since swRegistration is undefined. Not running in pwa mode?');
    }

    if (!navigator.onLine) {
      throw Error('webpush subscription not performed because we are offline');
    }
    try {
      const subscribeParams = { userVisibleOnly: true, applicationServerKey: publicVapidKey };
      console.log('publicVapidKey', publicVapidKey);

      // the subscribe method fails for example on lvb jump server because no fcm connection can be established. However, no exception is thrown and thus not caught below!!!!
      let subscription = await swRegistration.pushManager.subscribe(subscribeParams);
      let givenUuid = localStorage.getItem(LOCAL_STORAGE_DEVICE_UUID);
      console.log('Created webpush subscription', JSON.stringify({ subscription, givenUuid }, null, 2));

      // Store the subscription
      let { os, browser } = config.browserInfo;
      let response = await axios.post('/device/register', { subscription, givenUuid, os, browser }, { timeout: 700 });
      if (response && response.data) {
        console.log('Successfully registered for web push', response.data);
      }
      localStorage.setItem(LOCAL_STORAGE_WEBPUSH_SUBSCRIPTION, JSON.stringify(subscription));
      if (!givenUuid) {
        if (response && response.data && response.data.deviceUUID) {
          localStorage.setItem(LOCAL_STORAGE_DEVICE_UUID, response.data.deviceUUID);
        }
      }
      return { success: true, msg: 'Created webpush subscription', subscription };
    } catch (err) {
      console.warn('Failed to subscribe for webpush. Can you connect with fcm?', err);
      if (resubscribeOnFailure) {
        console.warn('Failed to create a webpush subscription. Try to re-subscribe', err);
        try {
          await this.resubscribe(swRegistration);
          return { success: true, msg: 'resubscribed successfully' };
        } catch (e) {
          throw new Error('Failed to subscribe for webpush. re-subscription failed', { cause: e });
        }
      } else {
        throw new Error('subscription for webpush failed definitely. No webpush messages can be received', { cause: err });
      }
    }
  },

  /**
   *
   * @param { ServiceWorkerRegistration} swRegistration
   */
  async resubscribe(swRegistration, publicVapidKey) {
    try {
      let subscription = await swRegistration.pushManager.getSubscription();
      let unsubscribeSuccess = await subscription.unsubscribe();
      if (unsubscribeSuccess) {
        console.log('Successfully unsubscribed for webpush');
      } else {
        console.log('Failed to unsubscribe from webpush. Ignore and try to subscribe');
      }
      let resubscribeOnFailure = false;
      await this.registerDeviceForWebPush(swRegistration, publicVapidKey, resubscribeOnFailure);
    } catch (err) {
      console.warn('re-subscribe failed', err);
    }
  },
};
